import moment from 'moment';
import { createSelector } from 'reselect';
import { extensions } from '../util/constants';
import {
  FhirIdentifier,
  Address,
  FhirArray,
  HumanName,
  ContactPoint,
  FhirDateTime,
  CodeableConcept,
  Extension,
} from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/Types';
import _ from 'lodash';
import { maskFHIR } from '@lifeomic/mask-data';
import { maskedModeSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/maskedMode';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { MrnConfigType } from '@lifeomic/phc-web-toolkit/src/common/types/Projects';

const age = (patient, mask) => {
  if (!patient.birthDate) return '';

  const birthDate =
    patient.birthDate.length < 5
      ? patient.birthDate + '-01-01'
      : patient.birthDate;
  const momentDate = moment(birthDate);
  if (!momentDate.isValid()) return '';

  let compDate = moment();
  if (patient.deceasedDate) {
    const deceasedDate = moment(patient.deceasedDate);
    if (deceasedDate.isValid()) {
      compDate = deceasedDate;
    }
  }

  const yearsOld = compDate.diff(momentDate, 'years');

  if (mask && yearsOld < 1) {
    return '< 1 year';
  }

  const daysOld = compDate.diff(momentDate, 'days');
  const weeksOld = compDate.diff(momentDate, 'weeks');
  const monthsOld = compDate.diff(momentDate, 'months');

  const pluralCheck = (rootWord, number) =>
    number > 1 ? `${rootWord}s` : rootWord;
  if (daysOld < 8) {
    return `${daysOld} ${pluralCheck('day', daysOld)}`;
  } else if (weeksOld < 5) {
    return `${weeksOld} ${pluralCheck('week', weeksOld)}`;
  } else if (monthsOld < 12) {
    return `${monthsOld} ${pluralCheck('month', monthsOld)}`;
  } else if (monthsOld < 60 && !mask) {
    const numYears = Math.floor(monthsOld / 12);
    const extraMonths = monthsOld % 12;

    const monthSpecifier =
      extraMonths === 0
        ? ''
        : ` ${extraMonths} ${pluralCheck('month', extraMonths)}`;
    return `${numYears} ${pluralCheck('year', numYears)}${monthSpecifier}`;
  } else {
    return `${mask ? '~' : ''}${yearsOld} ${pluralCheck('year', yearsOld)}`;
  }
};

const getMR = (patient, mrnConfig) => {
  const coding =
    mrnConfig.configType === MrnConfigType.typeCoding
      ? {
          typeSystem: mrnConfig.system,
          typeCode: mrnConfig.code,
        }
      : {
          typeSystem: extensions.patient.hl7.fhir.identifierSystemCode,
          typeCode: 'MR',
        };
  const mrOptions = {
    identifier: {
      ...coding,
      ...(mrnConfig.configType === MrnConfigType.system && {
        system: mrnConfig.system,
      }),
    },
    extension: {
      url: extensions.patient.hl7.fhir.renderedValue,
    },
  };

  const mrParser = new FhirIdentifier(mrOptions);
  const identifiers = _.get(patient, 'identifier', []);
  return mrParser.getDisplayValueList(identifiers);
};

const getSocialSecurityNumber = patient => {
  const parseOptions = {
    identifier: {
      typeSystem: extensions.patient.hl7.fhir.identifierSystemCode,
      typeCode: 'SS',
    },
  };

  const identifierParser = new FhirIdentifier(parseOptions);
  const identifiers = _.get(patient, 'identifier', []);
  return identifierParser.getDisplayValue(identifiers);
};

const getExtension = (patient, url) => {
  const parseOptions = {
    extension: {
      url,
    },
  };
  const extensionParser = new Extension(parseOptions);
  const extension = _.get(patient, 'extension', []);
  return extensionParser.getDisplayValue(extension);
};

const getRace = patient =>
  getExtension(patient, [
    extensions.patient.hl7.fhir.usCoreRace,
    extensions.patient.hl7.fhir.usCore.usCoreRace,
    extensions.patient.fhir.argonaut.argoRace,
  ]);

const getGenderIdentity = patient =>
  getExtension(patient, [extensions.patient.hl7.fhir.genderIdentity]);

const getEthnicity = patient =>
  getExtension(patient, [
    extensions.patient.hl7.fhir.usCoreEthnicity,
    extensions.patient.hl7.fhir.usCore.usCoreEthnicity,
    extensions.patient.fhir.argonaut.argoEthnicity,
  ]);

const getBirthsex = patient =>
  getExtension(patient, [extensions.patient.hl7.fhir.usCoreBirthsex]);

const getBirthplace = patient =>
  getExtension(patient, [extensions.patient.hl7.fhir.birthPlace]);

const getAddress = patient => {
  const parser = new FhirArray({
    fhirArray: {
      arrayElementParser: new Address({
        address: {
          omitLineBreaks: true,
          lineBreakString: ', ',
        },
      }),
    },
  });

  const rawValue = _.get(patient, 'address');
  return parser.getDisplayValue(rawValue);
};

const getName = (patient, humanNameProps) => {
  const parser = new FhirArray({
    fhirArray: {
      arrayElementParser: new HumanName(humanNameProps),
    },
  });

  const rawValue = _.get(patient, 'name');
  return parser.getDisplayValue(rawValue);
};

const getPhone = (patient, contactPointProps) => {
  const parser = new FhirArray({
    fhirArray: {
      arrayElementParser: new ContactPoint(contactPointProps),
      concatenate: true,
    },
  });

  const rawValue = _.get(patient, 'telecom', [])
    .filter((telecom = {}) => telecom.system === 'phone')
    .filter(Boolean);

  return parser.getDisplayValue(rawValue);
};

const getEmail = (patient, contactPointProps) => {
  const parser = new FhirArray({
    fhirArray: {
      arrayElementParser: new ContactPoint(contactPointProps),
      concatenate: true,
    },
  });

  const rawValue = _.get(patient, 'telecom', [])
    .filter((telecom = {}) => telecom.system === 'email')
    .filter(Boolean);
  return parser.getDisplayValue(rawValue);
};

const getBirthdateDisplay = patient => {
  const parser = new FhirDateTime();

  const rawValue = _.get(patient, 'birthDate');
  return parser.getDisplayValue(rawValue);
};

const getDeceasedDateDisplay = patient => {
  const parser = new FhirDateTime();

  const rawValue = _.get(patient, 'deceasedDateTime');
  return parser.getDisplayValue(rawValue);
};

const getMaritalStatus = patient => {
  const parser = new CodeableConcept();

  const rawValue = _.get(patient, 'maritalStatus');
  return parser.getDisplayValue(rawValue);
};

const getANON = patient => {
  const parseOptions = {
    identifier: {
      typeSystem: extensions.patient.hl7.fhir.identifierSystemCode,
      typeCode: 'ANON',
    },
  };
  const identifierParser = new FhirIdentifier(parseOptions);
  const identifiers = _.get(patient, 'identifier', []);
  return identifierParser.getDisplayValue(identifiers);
};

const getProjectId = patient => {
  const tags = _.get(patient, 'meta.tag', []);

  if (tags.length === 0) {
    return null;
  }

  const datasetSystem = tags.find(
    m => m.system === 'http://lifeomic.com/fhir/dataset',
  );

  return datasetSystem ? datasetSystem.code : null;
};

const patientResponse = state => _.get(state, 'fhir.patient.response', null);
const patientPropsSelector = (patient, mask, mrnConfig) => {
  const patientHasPhoto = _.get(patient, 'photo.length', 0) > 0;
  if (patient && mask) {
    patient = maskFHIR(patient);
  }

  return {
    id: _.get(patient, 'id', null),
    identifier: _.get(patient, 'identifier', []),
    extension: _.get(patient, 'extension', null),
    address: getAddress(patient),
    name: {
      given: getName(patient, { humanName: { givenOnly: true } }),
      prefix: getName(patient, { humanName: { prefixOnly: true } }),
      family: getName(patient, { humanName: { familyOnly: true } }),
      display: getName(patient),
    },
    phone: getPhone(patient),
    projectId: getProjectId(patient),
    email: getEmail(patient, { contactPoint: { omitUse: true } }),
    mr: getMR(patient, mrnConfig),
    ssn: getSocialSecurityNumber(patient),
    anonymousIdentifier: getANON(patient),
    gender: _.get(patient, 'gender', null),
    genderIdentity: getGenderIdentity(patient),
    birthDate: _.get(patient, 'birthDate', null),
    birthDateDisplayValue: getBirthdateDisplay(patient),
    deceasedDate: _.get(patient, 'deceasedDateTime', null),
    deceasedDateDisplayValue: getDeceasedDateDisplay(patient),
    isDeceased:
      !!_.get(patient, 'deceasedDateTime', null) ||
      _.get(patient, 'deceasedBoolean', false),
    maritalStatus: getMaritalStatus(patient),
    multipleBirthInteger: _.get(patient, 'multipleBirthInteger', null),
    photo: _.map(_.get(patient, 'photo', []), p => ({
      contentType: p.contentType,
      data: p.data,
    })),
    patientHasPhoto,
    communication: _.get(patient, 'communication', null),
    response: patient,
    race: getRace(patient),
    ethnicity: getEthnicity(patient),
    birthsex: getBirthsex(patient),
    birthPlace: getBirthplace(patient),
  };
};

const patient = createSelector(
  patientResponse,
  maskedModeSelectors.selectShouldMask,
  projectPickerSelectors.selectActiveMrnConfig,
  patientPropsSelector,
);

export { patient, patientPropsSelector, age };
